import type { ReactNode } from 'react'
import { Component } from 'react'
import { AppError, type AppErrorMessages, getErrorReference } from './app-error'

/**
 * What the boundary knows about a crash. `reference` is the support reference
 * the user is shown - a server reference when the error carries one, a client
 * generated one otherwise. It is what a user quotes, so it must reach whatever
 * system records crashes.
 */
export type BoundaryErrorInfo = {
  reference?: string
  componentStack?: string
}

/**
 * Sentences naming WHAT failed, shown when the caught error describes a
 * route-level 404 or 403 rather than an unexpected crash. Optional: without
 * them the boundary falls back to the kind's own message copy.
 */
export type RouteFailureSentences = {
  notFound?: string
  forbidden?: string
}

type State = { error: unknown | null; reference: string | null; componentStack?: string }

/**
 * Which route-level failure an error describes, if any. Route guards never
 * throw - they `redirect` - so what reaches this boundary is something THROWN:
 * an `ApiError` from a loader or beforeLoad, or a status-bearing object rethrown
 * at render time. A 404 or a 403 has a plain-language answer ("this page could
 * not be loaded" / "access was denied"); only an unexpected crash deserves the
 * generic copy.
 */
function routeFailureKind(error: unknown): 'notFound' | 'forbidden' | null {
  if (typeof error !== 'object' || error === null) return null
  const candidate = error as Record<string, unknown>
  if (candidate.kind === 'notFound' || candidate.status === 404) return 'notFound'
  if (candidate.kind === 'forbidden' || candidate.status === 403) return 'forbidden'
  return null
}

export class AppErrorBoundary extends Component<
  {
    children: ReactNode
    messages: AppErrorMessages
    routeFailureSentences?: RouteFailureSentences
    onError?: (error: unknown, info: BoundaryErrorInfo) => void
  },
  State
> {
  override state: State = { error: null, reference: null }

  static getDerivedStateFromError(error: unknown): State {
    return { error, reference: getErrorReference(error) ?? null }
  }

  override componentDidCatch(error: unknown, info: { componentStack?: string | null }) {
    const componentStack = typeof info.componentStack === 'string' ? info.componentStack : undefined
    this.props.onError?.(error, {
      ...(this.state.reference ? { reference: this.state.reference } : {}),
      ...(componentStack ? { componentStack } : {}),
    })
  }

  override render() {
    const failure = this.state.error ? routeFailureKind(this.state.error) : null

    if (!this.state.error) return this.props.children

    // An unexpected crash keeps today's wording exactly: generic title and
    // body, the reference code, retry/reload.
    if (failure === null) {
      return (
        <AppError
          error={this.state.error}
          messages={this.props.messages}
          reference={this.state.reference ?? undefined}
        />
      )
    }

    // A route-level 404/403 is a named answer, not a crash. Coerce the kind so
    // AppError renders its not-found / access-denied chrome instead of the
    // generic crash copy, and say WHAT could not be loaded when the app
    // supplies the sentence. The reference code survives either way - it is
    // whatever getDerivedStateFromError recorded.
    return (
      <AppError
        error={{ ...(this.state.error as Record<string, unknown>), kind: failure }}
        messages={{
          ...this.props.messages,
          body: {
            ...this.props.messages.body,
            [failure]:
              this.props.routeFailureSentences?.[failure] ??
              this.props.messages.body[failure] ??
              this.props.messages.body.generic,
          } as Record<string, string>,
        }}
        reference={this.state.reference ?? undefined}
      />
    )
  }
}
